import {Trans} from '@lingui/macro';
import {chain} from 'lodash';
import memoize from 'lru-memoize';
import React, {FC, useState} from 'react';
import styled, {css} from 'styled-components';

import {addQueryToPath} from '../../../../../../../libs/shared/helpers/src/browser/urlHelpers';
import {useInitialEffect} from '../../../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {
  Handler,
  HandlerOf,
  PredicateOf,
} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {DropdownProps} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {PaletteColorsEnum} from '../../../../../../../libs/shared/ui/src/styles/paletteModel';
import {
  buildCancelTokenSource,
  CancelError,
  CancelTokenSource,
} from '../../../../../../helpers/asyncCancelHelpers';
import {BulkActionTypesEnum} from '../../../../../../helpers/bulkActionHelpers';
import {doesTeammateMatchQuery} from '../../../../../../helpers/modelHelpers';
import {ApiSearchTeammates} from '../../../../../../models/api/searchTeammateModel';
import {TeammateModel} from '../../../../../../models/api/teammateModel';
import {fetchApiAsync} from '../../../../../../services/http/http';
import {FindOnlineStatus} from '../../../../../../store/data/teammates/onlineTeammatesSelectors';
import {DropdownCategory} from '../../../../../common/dropdowns/dropdownCategory';
import {DeprecatedSearchDropdown} from '../../../../../common/dropdowns/search/deprecatedSearchDropdown';
import {RowRendererOf} from '../../../../../common/dropdowns/source/dropdownSource';
import {SyncDropdownSource} from '../../../../../common/dropdowns/source/syncDropdownSource';
import {BulkActionConfirmationDialog} from '../../actions/bulk/bulkActionConfirmationDialog';
import {AssignDropdownAddTeammateCardContainer} from './assignDropdownAddTeammateCardContainer';
import {AssignDropdownItem} from './assignDropdownItem';
import {AssignDropdownUnassignItem} from './assignDropdownUnassignItem';

const FOOTER_DROPDOWN_ITEM_COUNT = 5;
export interface AssignOptions {
  teammate: TeammateModel | undefined;
  openedAt: number | undefined;
  interfaceEventSection: string;
}

enum AssignDropdownItemTypesEnum {
  RECENT_ASSIGNEE,
  RECENT_ASSIGNEE_HEADER,
  UNASSIGNED_ASSIGNEE,
  TEAMMATE_ASSIGNEE,
  TEAMMATE_ASSIGNEE_HEADER,
}

interface DropdownRecentAssigneeItem {
  type: AssignDropdownItemTypesEnum.RECENT_ASSIGNEE;
  teammate: TeammateModel;
}

interface DropdownRecentAssigneeHeaderItem {
  type: AssignDropdownItemTypesEnum.RECENT_ASSIGNEE_HEADER;
}

interface DropdownUnassignedItem {
  type: AssignDropdownItemTypesEnum.UNASSIGNED_ASSIGNEE;
}

interface DropdownTeammateAssigneeItem {
  type: AssignDropdownItemTypesEnum.TEAMMATE_ASSIGNEE;
  teammate: TeammateModel;
}

interface DropdownTeammateAssigneeHeaderItem {
  type: AssignDropdownItemTypesEnum.TEAMMATE_ASSIGNEE_HEADER;
}

type AssigneeDropdownItem =
  | DropdownRecentAssigneeItem
  | DropdownRecentAssigneeHeaderItem
  | DropdownUnassignedItem
  | DropdownTeammateAssigneeItem
  | DropdownTeammateAssigneeHeaderItem;

/*
 * Props.
 */

interface AssignDropdownProps extends DropdownProps {
  user: TeammateModel;
  teammates: ReadonlyArray<TeammateModel>;
  isTeamMember: PredicateOf<TeammateModel>;
  findOnlineStatus: FindOnlineStatus;
  assigneeAll?: TeammateModel;
  onAssign: HandlerOf<AssignOptions>;
  onBulkAssign: HandlerOf<AssignOptions>;
  /**
   * Links to the page in settings where the user can invite a teammate, or
   * opens the invite teammate popover.
   */
  navigateToAddTeammate: Handler | undefined;
  isBulkActionRequired: boolean;
  conversationInboxIds: ReadonlyArray<number> | null;
}

/*
 * Style.
 */

interface FooterWrapperStyleProps {
  $dropdownItemsCount: number;
}
const StyledFooterWrapperDiv = styled.div<FooterWrapperStyleProps>`
  ${(p) =>
    p.$dropdownItemsCount <= FOOTER_DROPDOWN_ITEM_COUNT
      ? css`
          padding-top: 75px;
        `
      : css`
          border-top: 1px solid ${p.theme.greys.shade30};
        `}
`;

/*
 * Component.
 */

export const AssignDropdown: FC<AssignDropdownProps> = (props) => {
  const [status, setAsyncStatus] = useState(AsyncStatusesEnum.LOADING);
  const [teammateToBulkAssign, setTeammateToBulkAssign] = useState<TeammateModel | null>();
  const [openedAt] = useState(performance.now());
  const [isSearching, setIsSearching] = useState(false);
  const [recentAssignees, setRecentAssignees] = useState<ReadonlyArray<TeammateModel>>();
  const [bulkAssignInterfaceSection, setBulkAssignInterfaceSection] = useState<string | undefined>(undefined);
  const [cancelTokenSource, setCancelTokenSource] = useState<CancelTokenSource | undefined>(undefined);

  /*
   * Lifecycle.
   */

  useInitialEffect(() => {
    const newCancelTokenSource = buildCancelTokenSource();
    setCancelTokenSource(newCancelTokenSource);
    fetchRecentlyUsedAssigneesAsync();
  });

  /*
   * Network.
   */

  const fetchRecentlyUsedAssigneesAsync = async () => {
    setAsyncStatus(AsyncStatusesEnum.LOADING);
    tryCancelFetch();

    const {conversationInboxIds, user} = props;
    if (!conversationInboxIds || conversationInboxIds.length === 0) {
      setAsyncStatus(AsyncStatusesEnum.LOADED);
      return;
    }

    try {
      const recentAssigneesUrl = addQueryToPath(`${user.url}/recent_assignees`, {
        inbox_ids: conversationInboxIds.join(','),
      });
      const fetchedRecentAssignees = await fetchApiAsync(ApiSearchTeammates, recentAssigneesUrl, {
        cancelToken: cancelTokenSource ? cancelTokenSource.token : undefined,
      });
      setAsyncStatus(AsyncStatusesEnum.LOADED);
      setRecentAssignees(fetchedRecentAssignees.teammates);
    } catch (error) {
      if (error instanceof CancelError) {
        return;
      }

      setAsyncStatus(AsyncStatusesEnum.FAILED);
    }
  };

  const tryCancelFetch = () => cancelTokenSource && cancelTokenSource.cancel();

  /*
   * Event handlers.
   */

  const onRequestClose = () => {
    setTeammateToBulkAssign((prevTeammateToBulkAssign) => {
      // If we have a confirmation open, don't close.
      if (prevTeammateToBulkAssign !== undefined) {
        return prevTeammateToBulkAssign;
      }

      // Otherwise, request close.
      if (props.onRequestClose) {
        props.onRequestClose();
      }

      return null;
    });
  };

  const onAssignRequest: HandlerOf<{
    teammate: TeammateModel | undefined;
    interfaceEventSection: string;
  }> = ({teammate, interfaceEventSection}) => {
    const {onAssign, isBulkActionRequired} = props;

    // If we need to do a bulk action, ask for confirmation.
    if (isBulkActionRequired) {
      setTeammateToBulkAssign(teammate || null);
      setBulkAssignInterfaceSection(interfaceEventSection);
      return;
    }

    // Otherwise, snooze.
    onAssign({teammate, openedAt, interfaceEventSection});
  };

  const onHideBulkAssignConfirmation = () => {
    setTeammateToBulkAssign(undefined);
    setBulkAssignInterfaceSection(undefined);
  };

  const onConfirmBulkAssign = () => {
    const teammate = teammateToBulkAssign || undefined;
    const section = bulkAssignInterfaceSection ?? '';

    onHideBulkAssignConfirmation();

    props.onBulkAssign({
      teammate,
      openedAt,
      interfaceEventSection: section,
    });

    if (props.onRequestClose) {
      props.onRequestClose();
    }
  };

  /*
   * Dropdown Items.
   */

  const makeDropdownItems = () => {
    const {assigneeAll, teammates} = props;

    const maybeAssignee = teammates.find((teammate) => teammate === assigneeAll);
    const assigneeItem: AssigneeDropdownItem | undefined = maybeAssignee
      ? {
          type: AssignDropdownItemTypesEnum.TEAMMATE_ASSIGNEE,
          teammate: maybeAssignee,
        }
      : undefined;
    const unassignedItem: AssigneeDropdownItem = {type: AssignDropdownItemTypesEnum.UNASSIGNED_ASSIGNEE};

    const allTeammatesHeader: AssigneeDropdownItem = {
      type: AssignDropdownItemTypesEnum.TEAMMATE_ASSIGNEE_HEADER,
    };
    const nonAssigneeTeammates = teammates.filter((teammate) => teammate !== assigneeAll);
    const teammateItems: AssigneeDropdownItem[] = nonAssigneeTeammates.map((teammate) => ({
      type: AssignDropdownItemTypesEnum.TEAMMATE_ASSIGNEE,
      teammate,
    }));

    const recentAssigneeItems: AssigneeDropdownItem[] =
      status === AsyncStatusesEnum.LOADED && recentAssignees && recentAssignees.length > 0
        ? makeRecentAssigneeItems(recentAssignees)
        : [];

    const nonAssigneeItems = [unassignedItem, ...recentAssigneeItems, allTeammatesHeader, ...teammateItems];

    return assigneeItem
      ? [assigneeItem as AssigneeDropdownItem].concat(...nonAssigneeItems)
      : nonAssigneeItems;
  };

  /*
   * Render Helpers.
   */

  const maybeRenderBulkAssignConfirmation = () => {
    if (teammateToBulkAssign === undefined) {
      return null;
    }

    return (
      <BulkActionConfirmationDialog
        actionType={teammateToBulkAssign === null ? BulkActionTypesEnum.UNASSIGN : BulkActionTypesEnum.ASSIGN}
        onConfirmClick={onConfirmBulkAssign}
        onRequestClose={onHideBulkAssignConfirmation}
      />
    );
  };

  const renderRow: RowRendererOf<AssigneeDropdownItem> = (item, index) => {
    const {user, assigneeAll, isTeamMember, findOnlineStatus} = props;

    // Render static rows.
    if (
      item.type !== AssignDropdownItemTypesEnum.RECENT_ASSIGNEE &&
      item.type !== AssignDropdownItemTypesEnum.TEAMMATE_ASSIGNEE
    ) {
      return renderStaticRow(item, index, assigneeAll);
    }

    const {teammate, type} = item;
    const teammateSection = type === AssignDropdownItemTypesEnum.RECENT_ASSIGNEE ? 'recent' : 'all_items';
    const interfaceEventSection = isSearching ? 'search_results' : teammateSection;
    return (
      <AssignDropdownItem
        key={teammate.id}
        index={index}
        teammate={teammate}
        isUser={teammate === user}
        isAssignee={teammate === assigneeAll}
        isTeamMember={isTeamMember(teammate)}
        onlineStatus={findOnlineStatus(teammate)}
        onSelect={() => onAssignRequest({teammate, interfaceEventSection})}
      />
    );
  };

  const renderStaticRow = (
    item: DropdownUnassignedItem | DropdownTeammateAssigneeHeaderItem | DropdownRecentAssigneeHeaderItem,
    index: number,
    assigneeAll?: TeammateModel,
  ) => {
    switch (item.type) {
      case AssignDropdownItemTypesEnum.RECENT_ASSIGNEE_HEADER:
        return (
          <DropdownCategory>
            <Trans id="dropdown-assignees-section-recently-used">Recently used</Trans>
          </DropdownCategory>
        );
      case AssignDropdownItemTypesEnum.TEAMMATE_ASSIGNEE_HEADER:
        return (
          <DropdownCategory>
            <Trans id="dropdown-assignees-section-all-teammates">All teammates</Trans>
          </DropdownCategory>
        );
      default:
        return (
          <AssignDropdownUnassignItem
            index={index}
            assignee={assigneeAll}
            iconName="none"
            color={PaletteColorsEnum.GREY}
            shouldHideBackground
            onSelect={() => onAssignRequest({teammate: undefined, interfaceEventSection: 'all_items'})}
          />
        );
    }
  };

  const maybeRenderAddTeammateCard = (dropdownItemsCount: number) => {
    const {navigateToAddTeammate} = props;

    if (!navigateToAddTeammate) {
      return null;
    }

    return (
      <StyledFooterWrapperDiv $dropdownItemsCount={dropdownItemsCount}>
        <AssignDropdownAddTeammateCardContainer onClick={navigateToAddTeammate} />
      </StyledFooterWrapperDiv>
    );
  };

  const filterRows = memoize(20)((items: ReadonlyArray<AssigneeDropdownItem>, query: string) => {
    // Shortcut for empty query.
    if (!query) {
      setIsSearching(false);
      return items;
    }

    // Otherwise, filter by name.
    const teammateItems = items.map((item) =>
      item.type === AssignDropdownItemTypesEnum.TEAMMATE_ASSIGNEE ? item : undefined,
    );
    setIsSearching(true);
    return chain(teammateItems)
      .compact() // Remove the static rows.
      .filter((item) => doesTeammateMatchQuery(item.teammate, query))
      .value();
  });

  /*
   * Render.
   */

  const dropdownItems = makeDropdownItems();
  const dropdownSource = new SyncDropdownSource<AssigneeDropdownItem>({
    items: dropdownItems,
    rowFilter: filterRows,
    renderRow,
  });

  return (
    <>
      <DeprecatedSearchDropdown
        {...props}
        footer={maybeRenderAddTeammateCard(dropdownItems.length)}
        title={<Trans id="dropdown-assign-title">Assign</Trans>}
        width={320}
        source={dropdownSource}
        onRequestClose={onRequestClose}
        maxContentHeight={296}
      />
      {maybeRenderBulkAssignConfirmation()}
    </>
  );
};

/*
 * Helpers.
 */

function makeRecentAssigneeItems(recentAssignees: ReadonlyArray<TeammateModel>): AssigneeDropdownItem[] {
  const recentAssigneesHeader: AssigneeDropdownItem = {
    type: AssignDropdownItemTypesEnum.RECENT_ASSIGNEE_HEADER,
  };
  const recentAssigneeItems: AssigneeDropdownItem[] = recentAssignees.map((teammate) => ({
    type: AssignDropdownItemTypesEnum.RECENT_ASSIGNEE,
    teammate,
  }));
  return [recentAssigneesHeader, ...recentAssigneeItems];
}
